import {
  StandardNextButton,
  StandardStepSubtitleText,
  StandardStepTitle,
  StepWrapper
} from '../../../components';
import FormField from '@ramsey-design-system/form-field';
import Input from '@ramsey-design-system/input';
import { yupResolver } from '@hookform/resolvers/yup';
import { emailInfoSchema } from '../../../schemas/emailSchema';
import { useWizard } from 'react-use-wizard';
import { ReferralFormData, useReferralContext } from '../../../context';
import { FieldErrors, useForm } from 'react-hook-form';
import { useGAFormTracking } from '../../../../../shared/hooks';
import { trackStepCompleted } from '../../../../../shared/analytics';
import {
  getFormName,
  useTrackStepError,
  useTrackStepStarted
} from '../../../utils';

const STEP_NAME = 'email_step';
const STEP_LABEL = "What's your email info?";

interface EmailStepFields {
  email: string;
}

function EmailStep() {
  const formRef = useGAFormTracking(STEP_NAME);
  const { nextStep, activeStep } = useWizard();
  const { referral, updateReferral } = useReferralContext();
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<EmailStepFields>({
    defaultValues: {
      ...referral
    },
    resolver: yupResolver(emailInfoSchema)
  });

  useTrackStepStarted(activeStep + 1, STEP_NAME, STEP_LABEL);
  const trackError = useTrackStepError(activeStep + 1, STEP_NAME, STEP_LABEL);

  async function onValidSubmit(referral: Partial<ReferralFormData>) {
    updateReferral(referral);
    trackStepCompleted({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      answer_labels: ['Email'],
      answer_values: [referral.email!],
      form_name: getFormName()
    });
    await nextStep();
  }

  function onInvalid(formErrors: FieldErrors<EmailStepFields>) {
    trackError('validation', formErrors.email?.message, 'email');
  }

  return (
    <StepWrapper>
      <form
        ref={formRef}
        onSubmit={handleSubmit(onValidSubmit, onInvalid)}
        noValidate
      >
        <StandardStepTitle>What's your email?</StandardStepTitle>
        <StandardStepSubtitleText>
          We'll use this to send the pro's contact info to{'\u00A0'}you.
        </StandardStepSubtitleText>

        <div className="space-y-2 mb-4">
          <FormField
            id="email"
            label="Email"
            size="lg"
            invalid={!!errors.email}
            errors={[errors.email?.message]}
          >
            {() => (
              <Input
                {...register('email')}
                type="email"
                invalid={!!errors.email}
                size="lg"
                id="email"
                data-testid="tax_email_input"
                autoComplete="email"
              />
            )}
          </FormField>
        </div>

        <StandardNextButton />
      </form>
    </StepWrapper>
  );
}

export { EmailStep };
